/**
 * Bootstrap-time context attributes attached to every `bootstrap_*` and
 * `editor_ready_time` Datadog RUM custom action.
 *
 * `BOOTSTRAP_CONTEXT` holds only the static part (captured once at module
 * evaluation, before any client-side routing). Callers must also spread
 * `captureConnectionContext()` to get a fresh network snapshot at measurement
 * time rather than at import time:
 *
 *   Datadog.endMeasure(action, { ...BOOTSTRAP_CONTEXT, ...captureConnectionContext() });
 *
 * `has_project_id`:
 *   - `true`  → the user landed on the editor with a `?projectId=...` query
 *               param. The SDK will FETCH the existing project. Bootstrap
 *               timings on this flow are typically higher (network round-trip
 *               + project hydration).
 *   - `false` → the user landed on a "new session" URL (typically
 *               `/upload?productConfigurationId=...`). The SDK will CREATE
 *               a fresh project. Bootstrap timings are typically lower.
 *
 * Not using `Datadog.setGlobalAttribute` on purpose: explicit per-event
 * attachment keeps the attribute scoped to bootstrap metrics only, avoids
 * tag pollution on every future custom action, and makes the intent visible
 * at each emission site.
 */
export const BOOTSTRAP_HAS_PROJECT_ID: boolean =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('projectId');

// Called fresh at each endMeasure() site so the network snapshot reflects
// conditions at the moment of the measurement, not at module-eval time.
// Uses the Network Information API (Chrome/Android only); gracefully absent on
// Safari/Firefox — those sessions get connection_effective_type:"unknown".
//
// `connection_quality_tier` coalesces effective_type + downlink + RTT into a
// single dashboard facet (@context.connection_quality_tier) usable as a
// template variable to compare loading times across network conditions.
//
// Tiers aligned with Lighthouse simulated-throttling presets (industry standard):
//   fast    → 4g + downlink ≥ 4 Mbps + RTT ≤ 150 ms  (≈ Lighthouse "Fast 4G")
//   medium  → 4g + downlink ≥ 1.5 Mbps + RTT ≤ 300 ms (≈ Lighthouse "Slow 4G")
//   slow    → 4g with worse signal; or sub-4g (3g/2g/slow-2g)
//   unknown → Network Information API absent (Safari/iOS, Firefox desktop)
//
// The 4g effectiveType itself already guarantees RTT < 270 ms and downlink
// > 0.7 Mbps (WICG/Chromium spec), so "slow" within 4g means the low end of
// that range. RTT is factored in because high latency hurts initial load even
// on high-downlink connections (e.g. satellite: 10 Mbps / 600 ms RTT).
function classifyConnectionQuality(effectiveType: string, downlink?: number, rtt?: number): 'fast' | 'medium' | 'slow' {
  if (effectiveType !== '4g') return 'slow';
  const hasDownlink = downlink != null;
  const hasRtt = rtt != null;
  if (hasDownlink && downlink >= 4 && (!hasRtt || rtt <= 150)) return 'fast';
  if (hasDownlink && downlink >= 1.5 && (!hasRtt || rtt <= 300)) return 'medium';
  return 'slow';
}

function captureConnectionContext(): Record<string, string | number | boolean> {
  if (typeof navigator === 'undefined') return { connection_quality_tier: 'unknown' };
  const nav = navigator as any;
  const conn = nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
  if (!conn) return { connection_quality_tier: 'unknown' };

  const effectiveType: string = conn.effectiveType ?? 'unknown';
  const ctx: Record<string, string | number | boolean> = {
    connection_effective_type: effectiveType,
    connection_quality_tier: classifyConnectionQuality(effectiveType, conn.downlink, conn.rtt),
  };
  if (conn.downlink != null) ctx.connection_downlink_mbps = conn.downlink;
  if (conn.rtt != null) ctx.connection_rtt_ms = conn.rtt;
  if (conn.saveData != null) ctx.connection_save_data = conn.saveData;
  return ctx;
}

export { captureConnectionContext };

export const BOOTSTRAP_CONTEXT = {
  has_project_id: BOOTSTRAP_HAS_PROJECT_ID,
};
